import { EmailListItem } from '../models/email-list-item'
import { logErrorToApi } from '@/taskpane/common/error-logging'
import storage from '@/taskpane/common/local-storage'
import configuration from '@/taskpane/common/configuration'
import office from '@/taskpane/officejs/office-wrapper'

const BASE_BACKOFF_MS = 30000
const MAX_BACKOFF_MS = 4 * 60 * 60 * 1000
const MAX_ATTEMPTS = 20

export interface QueueEntry extends Omit<EmailListItem, 'sentTime'> {
  sentTime: number
  attempts: number
  nextAttemptAt: number
  changeKey?: string
}

export default class SentItemsQueue {
  private entries: QueueEntry[] = []
  private hydrated = false
  private storageKey = ''

  private setStorageKey() {
    const mailbox = office.profile.username() || 'unknown'
    this.storageKey = `${configuration.SENT_ITEMS_PENDING_QUEUE_KEY}:${mailbox.toLowerCase()}`
  }

  hydrate(): void {
    if (this.hydrated) {
      return
    }
    this.hydrated = true
    this.setStorageKey()

    const raw = storage.getLocalItem(this.storageKey)
    if (!raw) {
      return
    }

    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      return
    }
    if (!Array.isArray(parsed)) {
      return
    }

    // Filter out entries persisted by a prior schema version (e.g. pre-cleanup builds
    // where sentTime was an ISO string, or missing attempts/nextAttemptAt). Dropped
    // entries are not lost — server-side pendingTracking still has them, and the next
    // pollOnce will re-match and re-enqueue with the correct shape.
    this.entries = (parsed as any[]).filter(
      (e) => e && typeof e.id === 'string' && typeof e.sentTime === 'number' && typeof e.attempts === 'number' && typeof e.nextAttemptAt === 'number',
    )
  }

  async enqueue(items: EmailListItem[]): Promise<void> {
    if (items.length === 0) {
      return
    }

    let changed = false
    const now = Date.now()

    for (const item of items) {
      if (this.entries.some((e) => e.id === item.id)) continue
      const entry: QueueEntry = { ...item, sentTime: item.sentTime.getTime(), attempts: 0, nextAttemptAt: now }
      this.entries.push(entry)
      this.logEntry(entry, 'Sent Item enqueued for processing')
      changed = true
    }

    if (changed) {
      await this.persist()
    }
  }

  peekDueEntries(now: number): QueueEntry[] {
    return this.entries.filter((e) => e.nextAttemptAt <= now)
  }

  findById(id: string): QueueEntry | undefined {
    return this.entries.find((e) => e.id === id)
  }

  async ack(id: string): Promise<void> {
    const entry = this.entries.find((e) => e.id === id)
    if (!entry) {
      return
    }

    this.entries = this.entries.filter((e) => e.id !== id)
    this.logEntry(entry, 'Sent Item successfully processed')
    await this.persist()
  }

  async markAttempted(id: string): Promise<void> {
    const entry = this.entries.find((e) => e.id === id)
    if (!entry) {
      return
    }

    entry.attempts += 1
    if (entry.attempts >= MAX_ATTEMPTS) {
      this.logEntry(entry, `Sent Item dead-lettered after ${entry.attempts} failed attempts`)
      this.entries = this.entries.filter((e) => e.id !== id)
    } else {
      const backoff = Math.min(BASE_BACKOFF_MS * 2 ** entry.attempts, MAX_BACKOFF_MS)
      entry.nextAttemptAt = Date.now() + backoff
    }
    await this.persist()
  }

  private logEntry(entry: QueueEntry, message: string): void {
    logErrorToApi('Sent Items Queue', message, null, undefined, {
      id: entry.id,
      sentTime: new Date(entry.sentTime).toISOString(),
      trackingId: entry.extendedProperties.emailGuid,
    })
  }

  private async persist(): Promise<void> {
    try {
      storage.setLocalItem(this.storageKey, JSON.stringify(this.entries))
    } catch (err) {
      console.error('SentItemsQueue: localStorage write failed', err)
    }
  }
}
